import { useCallback } from 'react';
import { ISurveyResponse } from '../shared/interfaces/ISurveyResponse';
import * as Excel from 'exceljs';
import SurveyUtils from '../shared/SurveyUtils';
import SurveyService from '../services/SurveyService';
import { ICustomSurveyEntry } from '../shared/interfaces/ICustomSurveyEntry';
import { ICustomSurveyQuestion } from '../shared/interfaces/ICustomSurveyQuestion';
import { ISurveyStatistics } from '../shared/interfaces/ISurveyStatistics';
import { findMatrixAnswer, getMatrixExportHeader } from '../shared/helpers/SurveyMatrix';
import { ISurveyResponseAnswer } from '../shared/interfaces/ISurveyResponsePage';

const useExportToExcel = () => {
  const normalizeGateAnswerForGermanExport = (
    feedback: string,
    question?: ICustomSurveyQuestion
  ) => {
    if (question?.type !== 'CUSTOMGATE') {
      return feedback;
    }

    const normalizedFeedback = feedback.trim().toLowerCase();

    if (normalizedFeedback === 'yes') {
      return 'Ja';
    }

    if (normalizedFeedback === 'no') {
      return 'Nein';
    }

    return feedback;
  };

  const addCell = (
    worksheet: any,
    column: string,
    row: number,
    content: string,
    center: boolean,
    bold: boolean
  ) => {
    worksheet.getCell(column + row).value = content;
    worksheet.getCell(column + row).font = { size: 10, bold: bold };
    if (center) {
      worksheet.getCell(column + row).alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
    }
    worksheet.getCell(column + row).border = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    };
  };

  const addCol = (current: string) => {
    const alphas = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    let carry = 1; // Start with a carry of 1 to increment the column

    // Process the column string from right to left
    for (let i = current.length - 1; i >= 0; i--) {
      const char = current[i];
      let charValue = alphas.indexOf(char);

      if (carry > 0) {
        charValue += carry;
        carry = 0; // Reset carry after adding it

        // Handle overflow (Z + 1 = A and carry 1)
        if (charValue >= alphas.length) {
          charValue -= alphas.length;
          carry = 1;
        }
      }
      result = alphas[charValue] + result;
    }

    // If there's still a carry after processing all characters, prepend 'A'
    if (carry > 0) {
      result = 'A' + result;
    }

    return result;
  };

  const addColX = (current: string, count: number) => {
    for (count > 0; count > 0; count--) {
      current = addCol(current);
    }
    return current;
  };

  const exportToExcel = useCallback(
    async (
      label: string,
      platform: string,
      companyName: string,
      slug: string,
      type: string,
      customAnswers: {
        answerOne: string;
        answerTwo: string;
        answerThree: string;
        answerFour: string;
        answerFive: string;
      },
      imageBase64: string,
      archived: boolean,
      archivedAt: string | null,
      surveyID: string,
      statistics: ISurveyStatistics
    ) => {
      const responseMap = new Map<string, { createdAt: string; answers: any[] }>();
      let after: string | undefined;
      do {
        const page = await SurveyService.getSurveyResponsePage(surveyID, { limit: 1000, after });
        if (!page) throw new Error('Could not load survey responses for export');
        page.responses.forEach((response) => {
          const existing = responseMap.get(response.responseKey) ?? {
            createdAt: response.createdAt,
            answers: [],
          };
          existing.answers.push(
            ...response.answers.map((answer) => ({ ...answer, createdAt: response.createdAt }))
          );
          responseMap.set(response.responseKey, existing);
        });
        after = page.nextCursor ?? undefined;
      } while (after);

      // Keep the existing worksheet writers' question-oriented input shape while the raw feed is paged.
      // Only the NPS/BAROMETER writer reads this, and those types store exactly one answer per response
      // (769607 plan fact 8), so compacting the per-question arrays cannot misalign two questions of the
      // same response. Do NOT reuse this for STANDARD or CUSTOMSURVEY: pairing answers by array index is
      // the defect S-3 removed. Look answers up by questionId off responseEntries instead.
      const surveyResponses = statistics.questions.map((question) => ({
        answers: Array.from(responseMap.values())
          .map((response) => response.answers.find((answer) => answer.questionId === question.id))
          .filter(Boolean),
        mood: question.mood,
        id: question.id,
        question: question.question,
        position: question.position,
        optional: question.optional,
        score: question.score,
        surveyId: surveyID,
        totalCount: question.totalCount,
      })) as unknown as ISurveyResponse[];
      const responseEntries = Array.from(responseMap.values());
      const endpointPath = SurveyUtils.getEndpointByType(type);
      const survey = await SurveyService.getSurveyPublic(surveyID, endpointPath);
      const hasLanguages = Object.keys(survey.i18n ?? []).length > 0;
      const hasPlatformUrl = Boolean(survey.platformUrl);
      const platformClickResponse = hasPlatformUrl
        ? await SurveyService.getSurveyPlatformClickCount(surveyID)
        : { count: 0 };
      const platformClicks = platformClickResponse?.count ?? 0;

      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet('Übersicht', {
        properties: { tabColor: { argb: 'FFFFFFFF' } },
        views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
      });
      const today = new Date();
      const formattedDate = today.toISOString().slice(0, 10);
      const title = `Reporting Reputationsmanagement | ${companyName} | ${
        archived ? archivedAt?.slice(0, 10) : formattedDate
      }`;
      const row = worksheet.getRow(13);
      let customResponses: any = {};
      let customSorted: ISurveyResponse[] = [];
      let customQuestionDetail: Record<string, ICustomSurveyQuestion> | undefined = {};

      // Custom-Berechnung start
      if (type === 'CUSTOMSURVEY') {
        const customDetail = (await SurveyService.getSurveyDetails({
          id: surveyID,
          type: type,
        })) as ICustomSurveyEntry;
        customQuestionDetail = customDetail?.questions?.reduce((acc, item) => {
          if (item.questionId) {
            acc[item.questionId] = item;
          }
          return acc;
        }, {} as Record<string, ICustomSurveyQuestion>);
        customSorted = statistics.questions as unknown as ISurveyResponse[];
        customResponses = Object.fromEntries(
          Array.from(responseMap.entries()).map(([key, response]) => [key, response.answers])
        );
        // Custom-Berechnung end
      }

      // second worksheet start
      if (type === 'CUSTOMSURVEY') {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });

        worksheetSecond.mergeCells('C1:Z1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        let currentColumn = 'B';

        // add the headers of the table
        addCell(worksheetSecond, currentColumn, 11, 'Datum', true, true);
        worksheetSecond.getColumn(currentColumn).width = 20;
        currentColumn = addCol(currentColumn);

        customSorted.forEach((question) => {
          const detail = customQuestionDetail?.[question.id];
          if (detail?.type === 'CUSTOMMATRIX') {
            (detail.rows ?? []).forEach((row, rowIndex) => {
              addCell(
                worksheetSecond,
                currentColumn,
                11,
                getMatrixExportHeader(
                  question.position as number,
                  question.question,
                  rowIndex,
                  row.label
                ),
                false,
                true
              );
              worksheetSecond.getColumn(currentColumn).width = 60;
              currentColumn = addCol(currentColumn);
            });
            return;
          }
          addCell(
            worksheetSecond,
            currentColumn,
            11,
            `Frage ${question.position} \n (${question.question})`,
            false,
            true
          );
          worksheetSecond.getColumn(currentColumn).width = 60;

          currentColumn = addCol(currentColumn);
        });

        addCell(worksheetSecond, currentColumn, 11, 'Auswertung', true, true);
        worksheetSecond.getColumn(currentColumn).width = 20;
        currentColumn = addCol(currentColumn);
        if (hasLanguages) {
          addCell(worksheetSecond, currentColumn, 11, 'Letzte Sprache', true, true);
          worksheetSecond.getColumn(currentColumn).width = 20;
        }

        // add the responses
        let customResponseRow = 12;

        const customResponseIds = Object.keys(customResponses);
        customResponseIds.sort((a, b) => {
          const ad = customResponses[a][0].createdAt;
          const bd = customResponses[b][0].createdAt;
          return ad > bd ? 1 : bd > ad ? -1 : 0;
        });

        customResponseIds.forEach((rid: any) => {
          const answers = customResponses[rid];

          currentColumn = 'B';
          addCell(
            worksheetSecond,
            currentColumn,
            customResponseRow,
            answers[0].createdAt.slice(0, 10),
            true,
            false
          );
          currentColumn = addCol(currentColumn);

          let lastLanguage;
          surveyResponses.forEach((question, index) => {
            const detail = customQuestionDetail?.[question.id];
            if (detail?.type === 'CUSTOMMATRIX') {
              (detail.rows ?? []).forEach((row) => {
                const answer = findMatrixAnswer<ISurveyResponseAnswer>(
                  answers,
                  question.id,
                  row.id
                );
                addCell(
                  worksheetSecond,
                  currentColumn,
                  customResponseRow,
                  answer?.feedback || '',
                  false,
                  false
                );
                currentColumn = addCol(currentColumn);
                if (answer?.language) lastLanguage = answer.language;
              });
              return;
            }
            const answer = answers.find((answer: any) => answer.questionId == question.id);

            if (!answer) {
              addCell(worksheetSecond, currentColumn, customResponseRow, '', false, false);
              currentColumn = addCol(currentColumn);
              return;
            }

            try {
              let answerData = JSON.parse(answer.feedback);
              answer.feedback = answerData.join(', ');
            } catch (e) {
              console.error(e);
            }

            answer.feedback = normalizeGateAnswerForGermanExport(
              answer.feedback,
              customQuestionDetail?.[answer.questionId]
            );

            if (answer && customQuestionDetail?.[answer.questionId].ShouldShowPrivacyNotice) {
              if (answer.has_accepted_privacy_notice === true) {
                answer.feedback += '\n (Datenschutzbestimmungen akzeptiert)';
              } else if (answer.has_accepted_privacy_notice === false) {
                answer.feedback += '\n (Datenschutzbestimmungen nicht akzeptiert)';
              } else {
                answer.feedback += '\n (Datenschutzbestimmungen nicht angegeben)';
              }
            }

            addCell(
              worksheetSecond,
              currentColumn,
              customResponseRow,
              answer?.feedback || '',
              false,
              false
            );
            currentColumn = addCol(currentColumn);

            if (answer?.language) lastLanguage = answer.language;
          });

          addCell(
            worksheetSecond,
            currentColumn,
            customResponseRow,
            SurveyUtils.isCompleted(answers, customSorted) ? 'Beendet' : 'Abgebrochen',
            true,
            false
          );

          if (hasLanguages) {
            currentColumn = addCol(currentColumn);

            addCell(
              worksheetSecond,
              currentColumn,
              customResponseRow,
              lastLanguage ?? 'Keine Angabe',
              true,
              false
            );
          }

          customResponseRow++;
        });
      } else if (type === 'STANDARD') {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });

        worksheetSecond.mergeCells('C1:H1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        worksheetSecond.mergeCells('C10:E10');
        worksheetSecond.getCell('C10').value = 'Rating';
        worksheetSecond.getCell('C10').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('C10').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('C10').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('F11').value = 'Auswertung';
        worksheetSecond.getCell('F11').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('F11').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('F11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('F').width = 20;

        worksheetSecond.getCell('B11').value = 'Datum';
        worksheetSecond.getCell('B11').font = { size: 10, bold: true };
        worksheetSecond.getCell('B11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('B11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('B').width = 20;

        worksheetSecond.getCell('C11').value = `Frage 1 \n (${surveyResponses[0].question})`;
        worksheetSecond.getCell('C11').font = { size: 10, bold: true };
        worksheetSecond.getCell('C11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('C').width = 60;

        worksheetSecond.getCell('D11').value = `Frage 2 \n (${surveyResponses[1].question})`;
        worksheetSecond.getCell('D11').font = { size: 10, bold: true };
        worksheetSecond.getCell('D11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('D').width = 60;

        worksheetSecond.getCell('E11').value = `Frage 3 \n (${surveyResponses[2].question})`;
        worksheetSecond.getCell('E11').font = { size: 10, bold: true };
        worksheetSecond.getCell('E11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('E').width = 50;

        worksheetSecond.getCell('G11').value = 'Feedback';
        worksheetSecond.getCell('G11').font = { size: 10, bold: true };
        worksheetSecond.getCell('G11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getColumn('G').width = 100;
        worksheetSecond.getCell('G11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        //loop start

        interface RowData {
          B: string;
          C: string | number;
          D: string | number;
          E: string;
          F: string;
          G: string | undefined;
        }

        const rowsData: RowData[] = [];

        const answerPattern = (answer?: { rating: number }) => {
          let cellValue;

          switch (answer?.rating) {
            case 1:
              cellValue = customAnswers.answerFive;
              break;
            case 2:
              cellValue = customAnswers.answerFour;
              break;
            case 3:
              cellValue = customAnswers.answerThree;
              break;
            case 4:
              cellValue = customAnswers.answerTwo;
              break;
            case 5:
              cellValue = customAnswers.answerOne;
              break;
            default:
              cellValue = 'no value';
          }
          return cellValue;
        };

        responseEntries.forEach((response) => {
          const first = response.answers.find(
            (answer) => answer.questionId === statistics.questions[0]?.id
          );
          const second = response.answers.find(
            (answer) => answer.questionId === statistics.questions[1]?.id
          );
          const gate = response.answers.find(
            (answer) => answer.questionId === statistics.questions[2]?.id
          );
          const summary = (first?.rating ?? 0) + (second?.rating ?? 0);
          const answerC = answerPattern(first);
          const answerD = answerPattern(second);
          const answerE = gate?.rating === 1 ? 'ja' : 'nein';

          const answer = { ...(gate ?? { feedback: '', rating: 0 }) };
          if (!answer.feedback) answer.feedback = '-';

          if (survey.feedbackActive && survey.shouldShowPrivacyNotice) {
            if (answer.has_accepted_privacy_notice === true) {
              answer.feedback += '\n (Datenschutzbestimmungen akzeptiert)';
            } else if (answer.has_accepted_privacy_notice === false) {
              answer.feedback += '\n (Datenschutzbestimmungen nicht akzeptiert)';
            } else {
              answer.feedback += '\n (Datenschutzbestimmungen nicht angegeben)';
            }
          }

          rowsData.push({
            B: response.createdAt.slice(0, 10),
            C: answerC,
            D: answerD,
            E: answerE,
            F:
              gate?.rating === 1 && summary >= statistics.questions[0]?.score
                ? 'Positiv'
                : 'Negativ',
            G: answer.feedback,
          });
        });

        rowsData.sort((a, b) => new Date(b.B).getTime() - new Date(a.B).getTime());

        rowsData.forEach((data, index) => {
          const rowIndex = index + 12;

          worksheetSecond.getCell(`B${rowIndex}`).value = data.B;
          worksheetSecond.getCell(`C${rowIndex}`).value = data.C;
          worksheetSecond.getCell(`D${rowIndex}`).value = data.D;
          worksheetSecond.getCell(`E${rowIndex}`).value = data.E;
          worksheetSecond.getCell(`F${rowIndex}`).value = data.F;
          worksheetSecond.getCell(`G${rowIndex}`).value = data.G;

          ['B', 'C', 'D', 'E', 'F', 'G'].forEach((col) => {
            const cell = worksheetSecond.getCell(`${col}${rowIndex}`);
            cell.font = { size: 10 };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center',
              wrapText: false,
              shrinkToFit: false,
            };
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            };
            worksheetSecond.getColumn('G').width = 100;
            worksheetSecond.getRow(rowIndex).height = 14;
            if (data.G && data.G.length > 100) {
              worksheetSecond.getCell(`G${rowIndex}`).alignment = {
                vertical: 'middle',
                horizontal: 'right',
              };
            }
          });
        });
      } else {
        const title = `Reporting Reputationsmanagement | ${companyName} | ${
          archived ? archivedAt?.slice(0, 10) : formattedDate
        }`;
        const worksheetSecond = workbook.addWorksheet('Details', {
          properties: { tabColor: { argb: 'FFFFFFFF' } },
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });
        const rowSecond = worksheetSecond.getRow(12);

        worksheetSecond.mergeCells('C1:H1');
        worksheetSecond.getCell('C1').value = title;
        worksheetSecond.getCell('C1').alignment = { horizontal: 'left', vertical: 'middle' };
        worksheetSecond.getCell('C1').font = {
          name: 'Sagoe UI',
          size: 14,
          bold: true,
          color: { argb: 'FF00C7CF' },
        };

        worksheetSecond.getCell('C10').value = 'Auswertung';
        worksheetSecond.getCell('C10').alignment = { horizontal: 'center', vertical: 'middle' };
        worksheetSecond.getCell('C10').font = { size: 10, bold: true, name: 'Sagoe UI' };
        worksheetSecond.getCell('C10').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('B11').value = 'Datum';
        worksheetSecond.getCell('B11').font = { size: 10, bold: true };
        worksheetSecond.getCell('B11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('B11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('B').width = 20;
        rowSecond.getCell('B').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('C11').value = 'Rating';
        worksheetSecond.getCell('C11').font = { size: 10, bold: true };
        worksheetSecond.getCell('C11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('C11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheetSecond.getColumn('C').width = 30;
        rowSecond.getCell('C').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getCell('D11').value = 'Feedback';
        worksheetSecond.getCell('D11').font = { size: 10, bold: true };
        worksheetSecond.getCell('D11').alignment = { vertical: 'middle', horizontal: 'center' };
        worksheetSecond.getCell('D11').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        worksheetSecond.getColumn('D').width = 100;
        rowSecond.getCell('D').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };

        //loop start

        interface RowData {
          B: string;
          C: string | number;
          D: string;
        }

        const rowsData: RowData[] = [];

        surveyResponses.forEach((response) => {
          response.answers.forEach((answer) => {
            if (!answer.createdAt) return;

            if (!answer.feedback) answer.feedback = '-';

            if (survey.feedbackActive && survey.shouldShowPrivacyNotice) {
              // @ts-expect-error
              if (answer.has_accepted_privacy_notice === true) {
                answer.feedback += '\n (Datenschutzbestimmungen akzeptiert)';
                // @ts-expect-error
              } else if (answer.has_accepted_privacy_notice === false) {
                answer.feedback += '\n (Datenschutzbestimmungen nicht akzeptiert)';
              } else {
                answer.feedback += '\n (Datenschutzbestimmungen nicht angegeben)';
              }
            }

            rowsData.push({
              B: answer.createdAt.slice(0, 10) || '-',
              C: response.mood || answer.rating || '-',
              D: answer.feedback ? answer.feedback : '-',
            });
          });
        });

        rowsData.sort((a, b) => new Date(b.B).getTime() - new Date(a.B).getTime());

        rowsData.forEach((data, index) => {
          const rowIndex = index + 12;

          worksheetSecond.getCell(`B${rowIndex}`).value = data.B;
          worksheetSecond.getCell(`C${rowIndex}`).value = data.C;
          worksheetSecond.getCell(`D${rowIndex}`).value = data.D;

          ['B', 'C', 'D'].forEach((col) => {
            const cell = worksheetSecond.getCell(`${col}${rowIndex}`);
            cell.font = { size: 10 };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center',
              wrapText: false,
              shrinkToFit: false,
            };
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            };
            if (data.D.length > 100) {
              worksheetSecond.getCell(`D${rowIndex}`).alignment = {
                vertical: 'middle',
                horizontal: 'right',
              };
            }
          });
          worksheetSecond.getColumn('D').width = 100;
        });
        //loop end
      }
      // second worksheet end

      // first worksheet start
      worksheet.mergeCells('C1:H1');
      worksheet.getCell('C1').value = title;
      worksheet.getCell('C1').alignment = { horizontal: 'fill', vertical: 'middle' };
      worksheet.getCell('C1').font = {
        name: 'Sagoe UI',
        size: 14,
        bold: true,
        color: { argb: 'FF00C7CF' },
      };

      let currentColumn = 'B';

      if (type != 'CUSTOMSURVEY') {
        worksheet.getCell(currentColumn + '12').value = 'Plattform';
        worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
        worksheet.getCell(currentColumn + '12').alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        worksheet.getCell(currentColumn + '12').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheet.getColumn(currentColumn).width = 20;
        worksheet.getRow(13).height = 30;
        row.getCell(currentColumn).border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        row.getCell(currentColumn).value = platform ? `${platform}` : '';
        currentColumn = addCol(currentColumn);
      }

      worksheet.getCell(currentColumn + '12').value = 'Umfragelabel';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 35;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value = label;

      currentColumn = addCol(currentColumn);
      const mergeCols =
        (type !== 'STANDARD' && type != 'CUSTOMSURVEY' ? 3 : 2) + (hasPlatformUrl ? 1 : 0);
      const mergeEndCol = addColX(currentColumn, mergeCols);
      worksheet.mergeCells(currentColumn + '11:' + mergeEndCol + '11');
      worksheet.getCell(currentColumn + '11').value = 'Auswertung';
      worksheet.getCell(currentColumn + '11').alignment = {
        horizontal: 'center',
        vertical: 'middle',
      };
      worksheet.getCell(currentColumn + '11').font = { size: 10, bold: true, name: 'Sagoe UI' };
      worksheet.getCell(currentColumn + '11').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };

      worksheet.getCell(currentColumn + '12').value = 'Gesamtzahl';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 15;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value = statistics.total.total;

      currentColumn = addCol(currentColumn);
      let labelPositive = 'Positiv';
      let labelNeutral = 'Neutral';
      let labelNegative = 'Negativ';

      if (type === 'CUSTOMSURVEY') {
        labelPositive = 'Beendet';
        labelNegative = 'Abgebrochen';
      }

      worksheet.getCell(currentColumn + '12').value = labelPositive;
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 10;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value = statistics.total.positive;
      currentColumn = addCol(currentColumn);

      if (type !== 'STANDARD' && type !== 'CUSTOMSURVEY') {
        worksheet.getCell(currentColumn + '12').value = labelNeutral;
        worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
        worksheet.getCell(currentColumn + '12').alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        worksheet.getCell(currentColumn + '12').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheet.getColumn(currentColumn).width = 10;
        row.getCell(currentColumn).border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        row.getCell(currentColumn).value = statistics.total.neutral;

        currentColumn = addCol(currentColumn);
      }

      worksheet.getCell(currentColumn + '12').value = labelNegative;
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 10;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      row.getCell(currentColumn).value = statistics.total.negative;

      currentColumn = addCol(currentColumn);

      if (hasPlatformUrl) {
        worksheet.getCell(currentColumn + '12').value = 'Klicks auf Plattform';
        worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
        worksheet.getCell(currentColumn + '12').alignment = {
          vertical: 'middle',
          horizontal: 'center',
        };
        worksheet.getCell(currentColumn + '12').border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        worksheet.getColumn(currentColumn).width = 18;
        row.getCell(currentColumn).border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        row.getCell(currentColumn).value = platformClicks;

        currentColumn = addCol(currentColumn);
      }

      worksheet.getCell(currentColumn + '12').value = 'URL zur Umfrage';
      worksheet.getCell(currentColumn + '12').font = { size: 10, bold: true };
      worksheet.getCell(currentColumn + '12').alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
      worksheet.getCell(currentColumn + '12').border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      worksheet.getColumn(currentColumn).width = 50;
      row.getCell(currentColumn).border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
      const urlPath = SurveyUtils.getPathByType(type);
      row.getCell(
        currentColumn
      ).value = `${process.env.REACT_APP_FRONTEND_BASE_URL}/${urlPath}/${slug}`;

      // first worksheet end

      function saveAs(blob: any, fileName: string) {
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      const fileName = `${label}_${platform}_${companyName}_${slug}.xlsx`;
      workbook.xlsx
        .writeBuffer()
        .then((buffer: any) => {
          const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
          saveAs(blob, fileName);
        })
        .catch((err: any) => console.error('Error writing excel export', err));
    },
    []
  );

  return exportToExcel;
};

export default useExportToExcel;
